import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { ruleText, isDerived } from './dataGraphApi';
import RuleDialog, { freshCandidates } from './RuleDialog';

// How a listed object is reached (contract 3.0, 2026-11-03; one cell of the object table since 2026-11-04,
// 4.2.4): its rules — `Object.column IN (Source.column)`, Source being the key table or another listed
// object — each tagged `derived` when the planner picked it at the Test (3.1) or `given`, with its "at most
// N per parent" cap. The cell only SHOWS them (4.2.8): Edit opens RuleDialog, where a derived rule is
// confirmed or replaced by one of the ranked alternatives, a cap set, a rule removed or added. The object
// itself is ticked in the table; nothing here adds or removes objects. Unsaved until the page's Save settings.

const lower = (s) => String(s || '').toLowerCase();

/**
 * `object`: the list entry `{ name, save, rules }`; `onUpdate(next)` replaces it. `keyTable`: the start.
 * `sources`: the objects a rule may read from. `candidates`: this object's ranked suggestions (getRuleSuggestions).
 * The Test's counts are in the Test panel, not here.
 */
const RuleCell = ({ object, keyTable, sources, columnsOf, disabled, candidates, onUpdate, testId }) => {
  const [editing, setEditing] = useState(false);
  const rules = object.rules || [];
  const isStart = lower(object.name) === lower(keyTable);
  const fresh = freshCandidates(object, candidates).length;
  return (
    <div className='dg-rulecell' data-testid={testId}>
      {isStart && <span className='dg-rule__origin dg-rule__origin--start' title='The key table: the subset’s selected rows'>start</span>}
      {rules.length === 0 && !isStart && (
        <span className='dg-muted' data-testid={`${testId}-reference`}>
          <span className='dg-rule__origin dg-rule__origin--new'>new</span> no rule yet — the Test derives one
        </span>
      )}
      {rules.map((r, i) => {
        const id = ruleText(object.name, r);
        return (
          <div className='dg-rule' key={id} data-testid={`${testId}-rule-${i}`}>
            <span className='dg-mono' title={id}>{id}</span>
            {isDerived(r) ? (
              <span className='dg-rule__origin' data-testid={`${testId}-rule-${i}-derived`}
                title='The planner’s pick for an object that had no rule: a default. The Test’s counts say whether it is the right one; Edit confirms or replaces it.'>
                derived
              </span>
            ) : (
              <span className='dg-rule__origin dg-rule__origin--given' title='A rule you chose or confirmed'>given</span>
            )}
            {r.maxPerParent != null && (
              <span className='dg-muted' data-testid={`${testId}-rule-${i}-cap`}>at most {r.maxPerParent} per parent</span>
            )}
          </div>
        );
      })}
      {object.save === false && (
        // "use, don't save" is hidden (2026-11-04): a subset stands on its own, so every object its rows point
        // at is saved. A graph that already has one keeps it, visibly; Save it puts it back.
        <span className='dg-muted' data-testid={`${testId}-nosave`}>
          <span className='dg-rule__origin dg-rule__origin--new'>not saved</span> its rows only feed rules
          {!disabled && <Button size='sm' variant='link' className='p-0 ml-1' data-testid={`${testId}-nosave-undo`} onClick={() => onUpdate({ ...object, save: true })}>Save it</Button>}
        </span>
      )}
      {!disabled && (
        <div className='dg-rulecell__actions'>
          <Button size='sm' variant='outline-secondary' className='dg-rulecell__edit' data-testid={`${testId}-edit`} onClick={() => setEditing(true)}
            title={fresh > 0 ? `Edit: ${fresh} alternative${fresh === 1 ? '' : 's'} from the profile’s links` : 'Edit the rules'}>
            Edit{fresh > 0 && rules.some(isDerived) ? ` · ${fresh} alternative${fresh === 1 ? '' : 's'}` : ''}
          </Button>
        </div>
      )}
      {!disabled && editing && (
        <RuleDialog show onHide={() => setEditing(false)} object={object} sources={sources} columnsOf={columnsOf}
          candidates={candidates} onUpdate={onUpdate} testId={testId} />
      )}
    </div>
  );
};

export default RuleCell;
